import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '@/contexts/LanguageContext';
import { useAuth } from '@/contexts/AuthContext';
import SearchBar from '@/components/SearchBar';
import QuickCodeLookup from '@/components/QuickCodeLookup';
import NearMeButton from '@/components/NearMeButton';
import PropertyCard from '@/components/PropertyCard';
import AdBanner from '@/components/AdBanner';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { ArrowRight, MapPin, Building2, Users, Shield, Star, ShieldCheck, CheckCircle2, BadgeCheck, ListChecks } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATE_BENTO_SPANS = [
  'md:col-span-2 md:row-span-2 aspect-square',
  'aspect-[3/4]',
  'aspect-[3/4]',
  'md:col-span-2 aspect-[2/1]',
];

export default function HomePage() {
  const { t, language } = useLanguage();
  const { user } = useAuth();
  const isSeeker = user?.role === 'user';
  const [properties, setProperties] = useState([]);
  const [featuredProperties, setFeaturedProperties] = useState([]);
  const [featuredIds, setFeaturedIds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statesWithCounts, setStatesWithCounts] = useState([]);

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/properties?limit=6`).then(res => setProperties(res.data.properties)),
      axios.get(`${API}/featured/active`).then(res => {
        setFeaturedProperties(res.data.properties || []);
        setFeaturedIds(res.data.featured_ids || []);
      }),
      axios.get(`${API}/states`).then(res => setStatesWithCounts(res.data.states || [])).catch(() => {})
    ]).catch(() => {}).finally(() => setLoading(false));
  }, []);

  return (
    <div>
      {/* Hero Section */}
      <section className="relative overflow-hidden bg-[#0B192C] min-h-[85vh] flex items-center" data-testid="hero-section">
        <div className="absolute inset-0">
          <img src="https://images.pexels.com/photos/28586227/pexels-photo-28586227.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=750&w=1260" alt="Hero"
            className="w-full h-full object-cover opacity-50" />
          <div className="absolute inset-0 bg-gradient-to-b from-[#0B192C]/70 via-[#0B192C]/30 to-[#0B192C]/80" />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 md:py-32 w-full">
          <div className="max-w-2xl">
            <p className="uppercase tracking-[0.25em] text-[#D4AF37] text-xs font-medium mb-4 inline-flex items-center gap-2" data-testid="hero-eyebrow">
              <ShieldCheck className="w-3.5 h-3.5" />
              {t('hero.eyebrow')}
            </p>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold text-white tracking-tight leading-tight" data-testid="hero-title">
              {t('hero.title')}
            </h1>
            <p className="mt-3 inline-flex items-center gap-2 text-[#D4AF37]/90 text-sm font-medium" data-testid="hero-highlight">
              <CheckCircle2 className="w-4 h-4" />
              {t('hero.highlight')}
            </p>
            <p className="mt-5 text-lg text-white/70 leading-relaxed">
              {t('hero.subtitle')}
            </p>
          </div>
          <div className="mt-10 max-w-5xl">
            <div className="backdrop-blur-xl bg-white/10 border border-white/20 rounded-2xl p-2">
              <SearchBar compact />
            </div>
            {/* Code lookup + Geolocation discovery — side-by-side */}
            <div className="mt-3 flex flex-col sm:flex-row items-stretch gap-2">
              <QuickCodeLookup variant="hero" />
              <NearMeButton variant="hero" />
            </div>
          </div>
        </div>
      </section>

      {/* Homepage Ad Banner */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-8">
        <AdBanner position="homepage_banner" />
      </div>

      {/* Featured / Promoted Properties */}
      {featuredProperties.length > 0 && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-12 pb-4" data-testid="promoted-section">
          <div className="flex items-center gap-2 mb-8">
            <Star className="w-5 h-5 text-[#D4AF37]" />
            <h2 className="font-heading text-2xl md:text-3xl font-semibold text-[#0B192C]">{language === 'es' ? 'Propiedades Destacadas' : 'Promoted Properties'}</h2>
            <Badge className="bg-[#D4AF37]/15 text-[#D4AF37] border border-[#D4AF37]/30 text-xs">{language === 'es' ? 'Patrocinado' : 'Sponsored'}</Badge>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {featuredProperties.map((prop, i) => (
              <div key={prop.id} className="relative" style={{ animationDelay: `${i * 0.08}s` }}>
                <div className="absolute top-3 right-12 z-10">
                  <Badge className="bg-[#D4AF37] text-[#0B192C] text-xs gap-1 shadow-md font-semibold"><Star className="w-3 h-3" />{language === 'es' ? 'Destacada' : 'Featured'}</Badge>
                </div>
                <PropertyCard property={prop} index={i} />
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Trust Pillars — Why WakaCasa */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-20" data-testid="trust-section">
        <div className="text-center max-w-2xl mx-auto mb-12">
          <p className="uppercase tracking-[0.2em] text-xs font-medium text-[#D4AF37] mb-3" data-testid="trust-eyebrow">
            {language === 'es' ? 'Por qué WakaCasa' : 'Why WakaCasa'}
          </p>
          <h2 className="font-heading text-2xl md:text-3xl font-semibold text-[#0B192C]" data-testid="trust-heading">
            {language === 'es' ? 'Compra con la confianza que mereces' : 'Buy with the confidence you deserve'}
          </h2>
          <p className="mt-3 text-sm text-slate-500 leading-relaxed">
            {language === 'es'
              ? 'Verificamos agentes y propiedades, y acompañamos cada paso de tu compra para que avances con confianza.'
              : 'WakaCasa verifies agents and properties, and walks with you through every step of your purchase so you move forward with confidence.'}
          </p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          {[
            {
              icon: ShieldCheck,
              title: language === 'es' ? 'Agentes Verificados' : 'Verified Agents',
              desc: language === 'es'
                ? 'Los agentes que se verifican pasan por revisión de cédula, selfie y licencia con IA y nuestro equipo. Su perfil lleva el sello "Verificado".'
                : 'Agents who choose to get verified go through ID, selfie and licence checks with AI plus our team — their profile carries a "Verified" badge.',
              testid: 'trust-pillar-agents',
              to: '/why/verified-agents',
            },
            {
              icon: BadgeCheck,
              title: language === 'es' ? 'Propiedades Verificadas' : 'Verified Properties',
              desc: language === 'es'
                ? 'Las propiedades publicadas por agentes verificados llevan el sello de propiedad verificada por WakaCasa.'
                : 'Listings from verified agents carry a WakaCasa-verified property seal so you know what you\'re looking at.',
              testid: 'trust-pillar-properties',
              to: '/why/verified-properties',
            },
            {
              icon: ListChecks,
              title: language === 'es' ? 'Compra Acompañada' : 'Tracked Transactions',
              desc: language === 'es'
                ? 'Seguimos cada paso de tu compra — desde la oferta y poderes hasta el registro y entrega de llaves.'
                : 'We track every step of your purchase — from offer and POA to registry and handover of keys.',
              testid: 'trust-pillar-transactions',
              to: '/why/tracked-transactions',
            },
          ].map((p, i) => (
            <Link
              key={i}
              to={p.to}
              className="group relative bg-white border border-slate-200 hover:border-[#D4AF37]/60 rounded-2xl p-6 transition-colors block"
              data-testid={p.testid}
            >
              <div className="absolute -top-px left-6 right-6 h-px bg-gradient-to-r from-transparent via-[#D4AF37] to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
              <div className="w-11 h-11 rounded-xl bg-[#0B192C] text-[#D4AF37] flex items-center justify-center mb-4 group-hover:scale-105 transition-transform">
                <p.icon className="w-5 h-5" />
              </div>
              <h3 className="font-heading font-semibold text-lg text-[#0B192C] mb-2">{p.title}</h3>
              <p className="text-sm text-slate-600 leading-relaxed">{p.desc}</p>
              <span className="mt-4 inline-flex items-center gap-1.5 text-xs font-semibold text-[#D4AF37] opacity-0 group-hover:opacity-100 transition-opacity">
                {language === 'es' ? 'Saber más' : 'Learn more'}
                <span aria-hidden>→</span>
              </span>
            </Link>
          ))}
        </div>
      </section>

      {/* Regular Featured Properties */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 md:py-24" data-testid="featured-section">
        <div className="flex items-end justify-between mb-10">
          <div>
            <p className="uppercase tracking-[0.2em] text-xs font-medium text-[#D4AF37] mb-2">{language === 'es' ? 'Explorar' : 'Explore'}</p>
            <h2 className="font-heading text-2xl md:text-3xl font-semibold text-[#0B192C]">{language === 'es' ? 'Propiedades Recientes' : 'Recent Properties'}</h2>
            <p className="mt-2 text-sm text-slate-500">{language === 'es' ? 'Las mejores propiedades en Venezuela' : 'Top properties across Venezuela'}</p>
          </div>
          <Link to="/search" data-testid="view-all-link">
            <Button variant="ghost" className="text-[#0B192C] hover:text-[#D4AF37] gap-1 font-medium">
              {t('common.viewAll')} <ArrowRight className="w-4 h-4" />
            </Button>
          </Link>
        </div>

        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[1,2,3].map(i => (
              <div key={i} className="rounded-xl overflow-hidden border border-border/50">
                <Skeleton className="aspect-[4/3]" />
                <div className="p-4 space-y-2"><Skeleton className="h-5 w-3/4" /><Skeleton className="h-4 w-1/2" /><Skeleton className="h-4 w-2/3" /></div>
              </div>
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {properties.map((prop, i) => (
              <div key={prop.id} className="animate-fade-in" style={{ animationDelay: `${i * 0.08}s` }}>
                <PropertyCard property={prop} index={i} />
              </div>
            ))}
          </div>
        )}
      </section>

      {/* Popular States - Bento Grid */}
      <section className="bg-[#0B192C] py-20 md:py-24" data-testid="states-bento-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="uppercase tracking-[0.2em] text-xs font-medium text-[#D4AF37] mb-2">{language === 'es' ? 'Destinos' : 'Destinations'}</p>
          <h2 className="font-heading text-2xl md:text-3xl font-semibold text-white mb-3">{language === 'es' ? 'Estados Populares' : 'Popular States'}</h2>
          <p className="text-sm text-white/50 mb-10">{language === 'es' ? 'Explora propiedades en los estados más buscados de Venezuela' : 'Explore properties in the most searched states of Venezuela'}</p>
          <div className="grid grid-cols-2 md:grid-cols-4 md:grid-rows-2 gap-4">
            {statesWithCounts.slice(0, 4).map((s, i) => (
              <Link key={s.key} to={`/state/${s.key}`} data-testid={`state-bento-card-${s.key}`}
                className={`group relative rounded-2xl overflow-hidden ${STATE_BENTO_SPANS[i]}`}>
                {s.image_url ? (
                  <img src={s.image_url.startsWith('http') ? s.image_url : `${API}/files/${s.image_url}`}
                    alt={s.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                ) : (
                  <div className="w-full h-full bg-gradient-to-br from-[#0B192C] via-[#142944] to-[#0B192C] flex items-center justify-center relative">
                    <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,rgba(212,175,55,0.18),transparent_60%)]" />
                    <div className="relative font-heading text-white/10 text-7xl font-bold tracking-tight select-none">{s.name?.[0] || '?'}</div>
                  </div>
                )}
                <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-black/15 to-transparent" />
                <div className="absolute bottom-5 left-5 right-5">
                  <div className="flex items-center gap-2 text-white">
                    <MapPin className="w-4 h-4 text-[#D4AF37]" />
                    <span className="font-heading font-semibold text-lg truncate">{s.name}</span>
                  </div>
                  <p className="text-xs text-white/70 mt-1">
                    <span className="text-[#D4AF37] font-semibold">{s.listing_count}</span> {language === 'es' ? 'propiedades' : 'listings'}
                    <span className="text-white/40"> · {s.city_count} {language === 'es' ? 'ciudades' : 'cities'}</span>
                  </p>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Browse by Estado */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 md:py-24" data-testid="states-section">
        <div className="flex items-end justify-between mb-10 flex-wrap gap-4">
          <div>
            <p className="uppercase tracking-[0.2em] text-xs font-medium text-[#D4AF37] mb-2">{language === 'es' ? 'Por Estado' : 'By State'}</p>
            <h2 className="font-heading text-2xl md:text-3xl font-semibold text-slate-900">{language === 'es' ? 'Explora por Estado' : 'Browse by State'}</h2>
            <p className="text-sm text-slate-500 mt-2 max-w-xl">{language === 'es' ? 'Cada estado de Venezuela tiene su propia historia inmobiliaria. Encuentra propiedades verificadas y agentes mejor calificados en tu estado.' : "Every Venezuelan state has its own real estate story. Find verified properties and top-rated agents in yours."}</p>
          </div>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
          {statesWithCounts.slice(0, 12).map((s, i) => (
            <Link key={s.key} to={`/state/${s.key}`} data-testid={`state-card-${s.key}`}
              className="group relative bg-white rounded-xl p-4 border border-border hover:border-[#D4AF37] hover:shadow-md transition-all">
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider font-semibold text-slate-400 mb-1.5">
                    <MapPin className="w-3 h-3" />
                    <span>#{i + 1}</span>
                  </div>
                  <h3 className="font-heading font-semibold text-slate-900 group-hover:text-[#0B192C] truncate">{s.name}</h3>
                  <p className="text-xs text-slate-500 mt-1">
                    <span className="text-[#D4AF37] font-semibold">{s.listing_count}</span> {language === 'es' ? 'propiedades' : 'listings'}
                    {s.city_count > 0 && <span className="text-slate-400"> · {s.city_count} {language === 'es' ? 'ciudades' : 'cities'}</span>}
                  </p>
                </div>
                <ArrowRight className="w-4 h-4 text-slate-300 group-hover:text-[#D4AF37] group-hover:translate-x-0.5 transition-all flex-shrink-0 mt-1" />
              </div>
            </Link>
          ))}
        </div>
        {statesWithCounts.length > 12 && (
          <div className="mt-6 flex flex-wrap gap-2 justify-center" data-testid="other-states">
            {statesWithCounts.slice(12).map(s => (
              <Link key={s.key} to={`/state/${s.key}`}>
                <Badge variant="outline" className="text-xs hover:bg-slate-50 cursor-pointer py-1 px-2.5">
                  {s.name} {s.listing_count > 0 && <span className="text-[#D4AF37] ml-1 font-semibold">{s.listing_count}</span>}
                </Badge>
              </Link>
            ))}
          </div>
        )}
      </section>

      {/* CTA Section */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 md:py-24" data-testid="cta-section">
        <div className="bg-[#0B192C] rounded-3xl p-10 md:p-16 relative overflow-hidden">
          <div className="absolute top-0 right-0 w-64 h-64 bg-[#D4AF37]/10 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2" />
          <div className="relative grid md:grid-cols-2 gap-10 items-center">
            <div>
              <p className="uppercase tracking-[0.2em] text-xs font-medium text-[#D4AF37] mb-3">{isSeeker ? (language === 'es' ? 'Para Compradores' : 'For Buyers') : (language === 'es' ? 'Para Agentes' : 'For Agents')}</p>
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-white mb-4">
                {isSeeker
                  ? (language === 'es' ? '¿Tienes propiedades para vender?' : 'Have properties to sell?')
                  : (language === 'es' ? 'Publica Tu Propiedad' : 'List Your Property')}
              </h2>
              <p className="text-white/60 mb-8 text-sm md:text-base leading-relaxed">
                {isSeeker
                  ? (language === 'es' ? 'Conviértete en Agente y comienza a publicar. Mantienes tu cuenta y favoritos.' : 'Switch to Agent mode and start listing. Keep your account and favorites.')
                  : (language === 'es' ? 'Llega a miles de compradores e inquilinos en Venezuela.' : 'Reach thousands of buyers and renters across Venezuela.')}
              </p>
              <Link to={isSeeker ? '/become-agent' : '/submit'}>
                <Button className="bg-[#D4AF37] hover:bg-[#E5C158] text-[#0B192C] rounded-full px-10 py-3 font-semibold shadow-lg" data-testid={isSeeker ? 'cta-become-agent-btn' : 'cta-submit-btn'}>
                  {isSeeker
                    ? (language === 'es' ? 'Convertirme en Agente' : 'Become an Agent')
                    : t('nav.submit')} <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
            </div>
            <div className="grid grid-cols-3 gap-6">
              {[
                { icon: Building2, label: language === 'es' ? 'Propiedades' : 'Properties', value: '500+' },
                { icon: Users, label: language === 'es' ? 'Usuarios' : 'Users', value: '10K+' },
                { icon: Shield, label: language === 'es' ? 'Verificadas' : 'Verified', value: '100%' },
              ].map((stat, i) => (
                <div key={i} className="text-center">
                  <stat.icon className="w-6 h-6 mx-auto mb-3 text-[#D4AF37]" />
                  <div className="font-heading font-bold text-2xl text-white">{stat.value}</div>
                  <div className="text-xs text-white/40 mt-1">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
